import DropFiles from "e.sic-platform.webclient/src/components/upload-drop-files/DropFiles";
import UploadTable from "e.sic-platform.webclient/src/components/upload-drop-files/UploadTable";
import { api } from "e.sic-platform.webclient/src/services/api/ApiService";
import { errorHandler } from "e.sic-platform.webclient/src/services/error-handler/ErrorHandlerService";
import {
  VideoAnalysisService,
  VideoAnalysisServiceModel,
} from "e.sic-platform.webclient/src/services/inspection/VideoAnalysisService";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import { uploadFilesService } from "e.sic-platform.webclient/src/services/upload/UploadFilesService";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, Stack } from "@mui/material";
import { DamagesStorageModel } from "e.sic-platform.webclient/src/services/damage/DamagesStorage";
import useUrlQueryParams from "e.sic-platform.webclient/src/services/url/UrlQueryService";
import SmartApp, { useSmartApps } from "../../components/apps/SmartApp";
import SmartAppStep from "../../components/apps/SmartAppStep";
import VideoAnalysisPlayer from "../../components/video-player/VideoAnalysisPlayer";
import UploadStatus from "e.sic-platform.webclient/src/components/upload-status/UploadStatus";
import { FileUploadTracker } from "e.sic-platform.webclient/src/services/upload/FileUploadTracker";
import RunVideoSegmentationButton from "../../components/buttons/run-video-segmentation-button/RunVideoSegmentationButton";
import { PubSubEvents } from "e.sic-platform.webclient/src/services/PubSubEvents";
import VideoTools from "e.sic-platform.webclient/src/pages/analysis/components/video-analysis-diagram/tools/VideoTools";
import { AnalysisConfidenceStorageModel } from "e.sic-platform.webclient/src/services/organization-analysis-confidence/AnalysisConfidenceStorage";
import PurchaseConfirmation from "../../components/apps/PurchaseConfirmation";
import { ActivityTypes } from "e.sic-platform.webclient/src/services/shop/ActivityPricesStorage";
import { creditBalanceStorage } from "e.sic-platform.webclient/src/services/shop/CreditBalanceStorage";
import CopyUrlButton from "../../components/buttons/copy-url-button/CopyUrlButton";
import LoadingSpinner from "e.sic-platform.webclient/src/components/loading-spinner/LoadingSpinner";

const emptyDamagesStorage = DamagesStorageModel.create({ inspectionId: 0 });
emptyDamagesStorage.mapResponse({ totalCount: 0, rows: [], header: [] });

export default observer(function VideoSegmentationPage() {
  return (
    <SmartApp
      titleKey="general.apps.video-segmentation.title"
      descriptionKey="videoSegmentation.description"
      image="/images/video-segmentation.png"
    >
      <Page />
    </SmartApp>
  );
});

const Page = observer(function VideoSegmentationPage() {
  useEffect(() => {
    uploadFilesService.clear();
  }, [])

  uploadFilesService.allowOnlySingleFile(true);
  const { setStep, confirmationPending, setConfirmationPending } =
    useSmartApps();
  const { id }: any = useUrlQueryParams();
  const [confidenceStorage] = useState(() =>
    AnalysisConfidenceStorageModel.create({ autoLoad: false }, { api })
  );
  const [videoSegmentation] = useState<VideoAnalysisService>(
    VideoAnalysisServiceModel.create({ videoId: id, language: translationService.language }, { api: api, errorHandler: errorHandler })
  );
  const canSegment = useMemo(
    () => uploadFilesService.filesForUpload.length !== 0,
    [uploadFilesService.filesForUpload.length]
  );

  const onUploadFinished = useCallback((file: FileUploadTracker) => {
      file.completed();
      videoSegmentation.setVideoId(file.id!);
      videoSegmentation.startSegmentation();
      videoSegmentation.reload();
  }, [videoSegmentation])

  const onConversionFinished = useCallback(async (id:number) => {
      if (id != videoSegmentation.videoId) return;
      videoSegmentation.reload();
  }, [videoSegmentation]);

  const onSegmentationFinished = useCallback(
    async (fileId: number) => {
      if (videoSegmentation.videoId != fileId) return;
      videoSegmentation.reload();
      await creditBalanceStorage.load();
    },
    [videoSegmentation, creditBalanceStorage]
  );

  const startSegmentationService = useCallback(async () => {
    uploadFilesService.startStandaloneUpload(
      onConversionFinished,
      onUploadFinished
    );
  }, [uploadFilesService, onConversionFinished, onUploadFinished]);

  useEffect(() => {
    if (videoSegmentation.videoId === undefined) return;
    var segmentationSubscribeToken = PubSub.subscribe(
      PubSubEvents.VideoSegmentationFinished,
      async (_event: string, notification: Notification) => {
        onSegmentationFinished(notification.data.fileId);
      }
    );

    return function cleanup() {
      PubSub.unsubscribe(segmentationSubscribeToken);
    };
  }, [onSegmentationFinished, videoSegmentation.videoId]);

  if (videoSegmentation.videoId) setStep(2);
  if (videoSegmentation.isSegmented) setStep(3);

  if (videoSegmentation && videoSegmentation.isLoading) return <Grid item xs={12}><LoadingSpinner /></Grid>

  return (
    <>
      <SmartAppStep
        titleKey="videoSegmentation.upload.title"
        descriptionKey="videoSegmentation.upload.description"
        visible={videoSegmentation.videoId === undefined}
        step={1}
      >
        <Grid item xs={12} sm={12}>
          <DropFiles
            singleFile
            onDroppedFiles={uploadFilesService.addFilesForUpload}
          />
        </Grid>
        <Grid item xs={12} sm={12} className="centered">
          {canSegment && (
            <UploadTable
              files={uploadFilesService.filesForUpload}
              onRemoveFile={uploadFilesService.removeFile}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={12} className="centered">
          <Stack direction="row" alignItems="center">
            <RunVideoSegmentationButton
              disabled={!canSegment}
              onClick={() => setConfirmationPending(!confirmationPending)}
            />
            <PurchaseConfirmation
              app={ActivityTypes.HighlightDetection}
              visible={confirmationPending}
              confirm={startSegmentationService}
              toggle={() => setConfirmationPending(!confirmationPending)}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={12}>
          {uploadFilesService.uploadProgressModalIsVisbile && <UploadStatus />}
        </Grid>
      </SmartAppStep>
      <SmartAppStep
        titleKey="videoSegmentation.viewVideo.title"
        descriptionKey="videoSegmentation.viewVideo.description"
        visible={videoSegmentation.videoId !== undefined && videoSegmentation.video !== undefined}
        step={2}
      >
        <Grid item xs={12} lg={8}>
          <VideoAnalysisPlayer videoAnalysis={videoSegmentation!} />
        </Grid>
        {videoSegmentation?.isSegmented && (
          <Grid item xs={12} lg={4} className="centered">
            <div className="legends-container">
              <VideoTools
                videoAnalysisService={videoSegmentation!}
                analysisConfidenceStorage={confidenceStorage}
              />
            </div>
          </Grid>
        )}
      </SmartAppStep>
      <SmartAppStep step={3} titleKey="videoSegmentation.viewSegmentation.title" descriptionKey="videoSegmentation.viewSegmentation.title" />
      {videoSegmentation.videoId !== undefined && (
        <Grid item xs={12}>
          <CopyUrlButton id={videoSegmentation.videoId}/>
        </Grid>
      )}
    </>
  );
});
